/**
 * PersonSearch - Verktyg för att upptäcka olovlig andrahandsuthyrning
 * Hemlig sida som endast nås via direktlänk
 */
import React, { useState, useRef } from 'react';
import { Box, Typography, TextField, Table, TableBody, TableCell,
         TableContainer, TableHead, TableRow, Alert, Chip,
         LinearProgress, Checkbox, Collapse, IconButton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import * as XLSX from 'xlsx';

import { Button } from '../../components/button';
import { H1 } from '../../components/typography';
import { COLORS } from '../../theme';

// Cloud Function URL - ändras baserat på miljö
const getApiUrl = () => {
  const hostname = window.location.hostname;
  const isDev = hostname === 'localhost' || hostname.includes('hyria-dev');
  const projectId = isDev ? 'hyria-dev' : 'hyria-58020';
  return `https://us-central1-${projectId}.cloudfunctions.net/personSearchProxy`;
};

// Styling constants
const SECTION_STYLES = {
  card: {
    backgroundColor: COLORS.white,
    borderRadius: '16px',
    padding: { xs: '16px', md: '24px' },
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
    border: `1px solid ${COLORS.darkGray}`,
    mb: 3
  },
  statBox: {
    textAlign: 'center',
    p: 2,
    bgcolor: COLORS.white,
    borderRadius: '12px',
    border: `1px solid ${COLORS.darkGray}`,
    minWidth: 100
  }
};

// Namnmatchning - hanterar olika ordning och mellannamn
const normalizeName = (firstName, lastName) => {
  const normalize = str => (str || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return normalize(firstName) + ' ' + normalize(lastName);
};

const namesMatch = (name1First, name1Last, name2First, name2Last) => {
  const n1 = normalizeName(name1First, name1Last);
  const n2 = normalizeName(name2First, name2Last);
  if (n1 === n2) return true;

  const n1Swapped = normalizeName(name1Last, name1First);
  const n2Swapped = normalizeName(name2Last, name2First);
  if (n1 === n2Swapped || n1Swapped === n2) return true;

  const parts1 = n1.split(' ').filter(p => p.length > 1);
  const parts2 = n2.split(' ').filter(p => p.length > 1);

  if (parts1.length >= 2 && parts2.length >= 2) {
    const shorter = parts1.length <= parts2.length ? parts1 : parts2;
    const longer = parts1.length <= parts2.length ? parts2 : parts1;
    const allShorterInLonger = shorter.every(shortPart =>
      longer.some(longPart =>
        longPart === shortPart ||
        longPart.includes(shortPart) ||
        shortPart.includes(longPart)
      )
    );
    if (allShorterInLonger) return true;
  }
  return false;
};

const normalizeApartmentNumber = (apt) => {
  if (!apt) return null;
  return String(apt).replace(/\D/g, '').replace(/^0+/, '') || null;
};

const splitFullName = (fullName) => {
  if (!fullName) return { firstName: '', lastName: '' };
  const name = String(fullName).trim();
  if (name.includes(',')) {
    const parts = name.split(',').map(p => p.trim());
    return { firstName: parts[1] || '', lastName: parts[0] || '' };
  }
  const parts = name.split(/\s+/);
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  const lastName = parts.pop();
  const firstName = parts.join(' ');
  return { firstName, lastName };
};

// Kollapserbar sektion-komponent
const CollapsibleSection = ({ title, count, color, bgColor, borderColor, icon, defaultExpanded, children }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <Box sx={{ mb: 2, overflow: 'hidden', borderRadius: '12px', border: `2px solid ${borderColor}` }}>
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          p: 2,
          bgcolor: bgColor,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          '&:hover': { opacity: 0.9 }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {icon}
          <Typography sx={{ color, fontWeight: 700, fontSize: '16px', fontFamily: "'Poppins', sans-serif" }}>
            {title}
          </Typography>
          <Chip
            label={count}
            size="small"
            sx={{
              bgcolor: color,
              color: COLORS.white,
              fontWeight: 700,
              fontSize: '0.9rem',
              minWidth: 32
            }}
          />
        </Box>
        <IconButton size="small" sx={{ color }}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        {children}
      </Collapse>
    </Box>
  );
};

export default function PersonSearch() {
  const [mode, setMode] = useState('single'); // single, multi, csv
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [zipcode, setZipcode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  const [stats, setStats] = useState(null);

  // Multi-adress läge
  const [multiAddresses, setMultiAddresses] = useState('');
  const [multiResults, setMultiResults] = useState([]);
  const [multiProgress, setMultiProgress] = useState({ current: 0, total: 0 });

  // CSV-läge
  const [csvData, setCsvData] = useState([]);
  const [csvColumns, setCsvColumns] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [nameFormat, setNameFormat] = useState('combined'); // combined, separate
  const [matchResults, setMatchResults] = useState([]);

  // Drag-and-drop
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  // Checkbox-markering
  const [selectedRows, setSelectedRows] = useState(new Set());

  const searchAddress = async (addressStreet, addressCity, addressZip) => {
    const s = addressStreet || street;
    const c = addressCity || city;
    const z = addressZip || zipcode;

    if (!s || !c) {
      return { success: false, error: 'Ange gatuadress och ort' };
    }

    try {
      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ street: s, city: c, zipcode: z || undefined })
      });

      const data = await response.json();
      return data;
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const handleSingleSearch = async () => {
    if (!street || !city) {
      setError('Ange gatuadress och ort');
      return;
    }

    setLoading(true);
    setError(null);
    setResults(null);

    const data = await searchAddress();

    if (!data.success) {
      setError(data.error || 'Sökningen misslyckades');
    } else {
      setStats({
        matching: data.matchingStreet,
        withApt: data.withApartment,
        withoutApt: data.withoutApartment,
        total: data.totalInArea,
        fetched: data.recordsFetched,
        hitLimit: data.hitLimit,
        warning: data.warning
      });
      setResults(data.residents);

      // Visa varning om vi träffade gränsen och hittade 0 matcher
      if (data.hitLimit && data.matchingStreet === 0) {
        setError(`Sökningen hittade ${data.totalInArea.toLocaleString()} personer i området men kunde bara hämta ${data.recordsFetched.toLocaleString()}. Ange postnummer för att hitta rätt adress!`);
      }
    }

    setLoading(false);
  };

  const handleMultiSearch = async () => {
    const lines = multiAddresses.split('\n').filter(line => line.trim());
    if (lines.length === 0) {
      setError('Ange minst en adress');
      return;
    }

    setLoading(true);
    setError(null);
    setMultiResults([]);
    setMultiProgress({ current: 0, total: lines.length });

    const results = [];
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      const match = line.match(/^(.+?)\s*,\s*(.+)$/) || line.match(/^(.+?)\s+(\w+)$/);

      let addrStreet = line;
      let addrCity = city || 'Stockholm';

      if (match) {
        addrStreet = match[1].trim();
        addrCity = match[2].trim();
      }

      setMultiProgress({ current: i + 1, total: lines.length });

      const data = await searchAddress(addrStreet, addrCity, '');

      results.push({
        address: line,
        street: addrStreet,
        city: addrCity,
        success: data.success,
        error: data.error,
        residents: data.residents || [],
        stats: data.success ? {
          matching: data.matchingStreet,
          withApt: data.withApartment,
          withoutApt: data.withoutApartment
        } : null
      });

      setMultiResults([...results]);
    }

    const totalStats = {
      addresses: results.length,
      successful: results.filter(r => r.success).length,
      totalResidents: results.reduce((sum, r) => sum + (r.residents?.length || 0), 0),
      withApt: results.reduce((sum, r) => sum + (r.stats?.withApt || 0), 0),
      withoutApt: results.reduce((sum, r) => sum + (r.stats?.withoutApt || 0), 0)
    };
    setStats(totalStats);

    setLoading(false);
  };

  // Drag-and-drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      processFile(file);
    }
  };

  const processFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1, raw: false, defval: '' });

        if (jsonData.length < 2) {
          setError('Filen innehåller inga rader');
          return;
        }

        const headers = jsonData[0].map((col, i) => String(col || '').trim() || `Kolumn ${i + 1}`);
        const rows = jsonData.slice(1).filter(row => row && row.some(cell => cell !== ''));

        setCsvColumns(headers);
        setCsvData(rows);

        const guesses = {
          name: headers.findIndex(col => /namn|name|medlem|full.*name/i.test(col)),
          firstName: headers.findIndex(col => /förnamn|first.*name|given/i.test(col)),
          lastName: headers.findIndex(col => /efternamn|last.*name|surname|family/i.test(col)),
          apartment: headers.findIndex(col => /lgh|lägenhet|apartment|apt/i.test(col)),
          address: headers.findIndex(col => /adress|address|gata|street/i.test(col))
        };

        if (guesses.firstName >= 0 && guesses.lastName >= 0) {
          setNameFormat('separate');
          setColumnMapping({
            firstName: guesses.firstName,
            lastName: guesses.lastName,
            apartment: guesses.apartment,
            address: guesses.address
          });
        } else {
          setNameFormat('combined');
          setColumnMapping({
            name: guesses.name >= 0 ? guesses.name : 0,
            apartment: guesses.apartment,
            address: guesses.address
          });
        }

      } catch (err) {
        setError('Kunde inte läsa filen: ' + err.message);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      processFile(file);
    }
  };

  const getColumnPreview = (columnIndex) => {
    if (columnIndex < 0 || csvData.length === 0) return [];
    return csvData.slice(0, 3).map(row => row[columnIndex] || '').filter(v => v);
  };

  const runCsvMatching = async () => {
    if (!street || !city) {
      setError('Ange gatuadress och ort för API-sökning');
      return;
    }

    if (nameFormat === 'combined' && (columnMapping.name === undefined || columnMapping.name < 0)) {
      setError('Välj kolumn för namn');
      return;
    }
    if (nameFormat === 'separate' && (columnMapping.firstName === undefined || columnMapping.firstName < 0 ||
        columnMapping.lastName === undefined || columnMapping.lastName < 0)) {
      setError('Välj kolumner för förnamn och efternamn');
      return;
    }

    setLoading(true);
    setError(null);
    setSelectedRows(new Set());

    try {
      const data = await searchAddress();
      if (!data.success) {
        setError(data.error || 'API-sökning misslyckades');
        setLoading(false);
        return;
      }

      let filteredCsvData = csvData;
      if (columnMapping.address >= 0) {
        const normalizedStreet = street.toLowerCase().replace(/[^a-zåäö0-9]/g, '');
        filteredCsvData = csvData.filter(row => {
          const rowAddress = (row[columnMapping.address] || '').toLowerCase().replace(/[^a-zåäö0-9]/g, '');
          return rowAddress.includes(normalizedStreet) || normalizedStreet.includes(rowAddress);
        });
      }

      const csvMembers = filteredCsvData.map((row, idx) => {
        let firstName, lastName, fullName;

        if (nameFormat === 'combined') {
          fullName = row[columnMapping.name];
          const parsed = splitFullName(fullName);
          firstName = parsed.firstName;
          lastName = parsed.lastName;
        } else {
          firstName = row[columnMapping.firstName] || '';
          lastName = row[columnMapping.lastName] || '';
          fullName = `${firstName} ${lastName}`.trim();
        }

        const apt = columnMapping.apartment >= 0 ? row[columnMapping.apartment] : null;
        return { firstName, lastName, fullName, apartmentNumber: apt, rowIndex: idx };
      }).filter(m => m.firstName || m.lastName);

      const results = [];
      const usedApiResidents = new Set();

      for (const csv of csvMembers) {
        const csvApt = normalizeApartmentNumber(csv.apartmentNumber);

        if (csvApt) {
          const apiMatch = data.residents.find(a =>
            normalizeApartmentNumber(a.apartmentNumber) === csvApt
          );

          if (apiMatch) {
            usedApiResidents.add(data.residents.indexOf(apiMatch));
            const nameMatches = namesMatch(csv.firstName, csv.lastName, apiMatch.firstName, apiMatch.lastName);
            results.push({
              csv, api: apiMatch,
              status: nameMatches ? 'match' : 'discrepancy',
              apartmentNumber: csvApt
            });
          } else {
            results.push({ csv, api: null, status: 'csv_only', apartmentNumber: csvApt });
          }
        } else {
          const apiMatch = data.residents.find((a, idx) => {
            if (usedApiResidents.has(idx)) return false;
            return namesMatch(csv.firstName, csv.lastName, a.firstName, a.lastName);
          });

          if (apiMatch) {
            usedApiResidents.add(data.residents.indexOf(apiMatch));
            results.push({
              csv, api: apiMatch,
              status: apiMatch.apartmentNumber ? 'filled' : 'match',
              apartmentNumber: normalizeApartmentNumber(apiMatch.apartmentNumber)
            });
          } else {
            results.push({ csv, api: null, status: 'csv_only', apartmentNumber: null });
          }
        }
      }

      data.residents.forEach((api, idx) => {
        if (!usedApiResidents.has(idx) && api.apartmentNumber) {
          results.push({
            csv: null, api,
            status: 'api_only',
            apartmentNumber: normalizeApartmentNumber(api.apartmentNumber)
          });
        }
      });

      setMatchResults(results);
      setStats({
        discrepancy: results.filter(r => r.status === 'discrepancy').length,
        match: results.filter(r => r.status === 'match' || r.status === 'filled').length,
        csvOnly: results.filter(r => r.status === 'csv_only').length,
        apiOnly: results.filter(r => r.status === 'api_only').length
      });

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fillExample = (s, c, z) => {
    setStreet(s);
    setCity(c);
    setZipcode(z);
  };

  // Checkbox-hantering
  const toggleRow = (index) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedRows(newSelected);
  };

  const selectAllInSection = (status) => {
    const newSelected = new Set(selectedRows);
    matchResults.forEach((r, i) => {
      if (r.status === status || (status === 'match' && r.status === 'filled')) {
        newSelected.add(i);
      }
    });
    setSelectedRows(newSelected);
  };

  const deselectAllInSection = (status) => {
    const newSelected = new Set(selectedRows);
    matchResults.forEach((r, i) => {
      if (r.status === status || (status === 'match' && r.status === 'filled')) {
        newSelected.delete(i);
      }
    });
    setSelectedRows(newSelected);
  };

  const handleImport = () => {
    const selectedItems = matchResults.filter((_, i) => selectedRows.has(i));
    alert(`Simulerar import av ${selectedItems.length} poster.\n\nI produktion skulle dessa läggas till i systemet.`);
  };

  const getStatusChip = (status) => {
    switch (status) {
      case 'discrepancy':
        return <Chip icon={<WarningIcon />} label="AVVIKELSE" sx={{ bgcolor: COLORS.red, color: COLORS.white }} size="small" />;
      case 'match':
        return <Chip icon={<CheckCircleIcon />} label="Match" sx={{ bgcolor: COLORS.green, color: COLORS.white }} size="small" />;
      case 'filled':
        return <Chip label="Lgh fyllt" sx={{ bgcolor: COLORS.primary, color: COLORS.white }} size="small" />;
      case 'csv_only':
        return <Chip label="Ej folkbokförd" sx={{ bgcolor: COLORS.lightOrange, color: COLORS.black }} size="small" />;
      case 'api_only':
        return <Chip label="Endast folkbokf." sx={{ bgcolor: COLORS.gray, color: COLORS.black }} size="small" />;
      default:
        return null;
    }
  };

  // Rendrera resultat-tabell för en sektion
  const renderResultTable = (status, includeCheckbox = true) => {
    const filteredResults = matchResults
      .map((r, i) => ({ ...r, originalIndex: i }))
      .filter(r => r.status === status || (status === 'match' && r.status === 'filled'))
      .sort((a, b) => (a.apartmentNumber || '9999').localeCompare(b.apartmentNumber || '9999', undefined, { numeric: true }));

    if (filteredResults.length === 0) {
      return (
        <Box sx={{ p: 3, textAlign: 'center', color: COLORS.lightBlack }}>
          Inga poster i denna kategori
        </Box>
      );
    }

    return (
      <>
        {includeCheckbox && (
          <Box sx={{ p: 1, borderBottom: `1px solid ${COLORS.darkGray}`, display: 'flex', gap: 1 }}>
            <Button
              backgroundColor={COLORS.white}
              textcolor={COLORS.black}
              onClick={() => selectAllInSection(status)}
              sx={{ fontSize: '12px', padding: '6px 12px' }}
            >
              Markera alla
            </Button>
            <Button
              backgroundColor={COLORS.white}
              textcolor={COLORS.black}
              onClick={() => deselectAllInSection(status)}
              sx={{ fontSize: '12px', padding: '6px 12px' }}
            >
              Avmarkera alla
            </Button>
          </Box>
        )}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {includeCheckbox && <TableCell padding="checkbox"></TableCell>}
                <TableCell>Lgh</TableCell>
                <TableCell>Medlemsregister (CSV)</TableCell>
                <TableCell></TableCell>
                <TableCell>Folkbokförd (API)</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredResults.map((result) => (
                <TableRow
                  key={result.originalIndex}
                  hover
                  sx={{
                    bgcolor: result.status === 'discrepancy' ? COLORS.lightRed : undefined
                  }}
                >
                  {includeCheckbox && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedRows.has(result.originalIndex)}
                        onChange={() => toggleRow(result.originalIndex)}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    {result.apartmentNumber ? (
                      <Chip label={result.apartmentNumber} sx={{ bgcolor: COLORS.primary, color: COLORS.white }} size="small" />
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    <strong>{result.csv ? result.csv.fullName : '-'}</strong>
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center', fontSize: 18, color: result.status === 'discrepancy' ? COLORS.red : COLORS.green }}>
                    {result.status === 'discrepancy' ? '≠' : '='}
                  </TableCell>
                  <TableCell sx={{ color: result.status === 'discrepancy' ? COLORS.red : undefined }}>
                    <strong>{result.api ? `${result.api.firstName} ${result.api.lastName}` : '-'}</strong>
                  </TableCell>
                  <TableCell>{getStatusChip(result.status)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, md: 3 } }}>
      <H1 sx={{ mb: 1 }}>Folkbokföringskontroll</H1>
      <Typography sx={{ color: COLORS.lightBlack, mb: 3, fontFamily: "'Poppins', sans-serif" }}>
        Verktyg för att upptäcka olovlig andrahandsuthyrning genom att jämföra medlemsregister med folkbokföringen.
      </Typography>

      {/* Lägesväljare */}
      <Box sx={{ mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {['single', 'multi', 'csv'].map((m) => (
          <Button
            key={m}
            onClick={() => { setMode(m); setError(null); }}
            backgroundColor={mode === m ? COLORS.black : COLORS.white}
            textcolor={mode === m ? COLORS.white : COLORS.black}
            sx={{
              border: `2px solid ${COLORS.black}`,
              '&:hover': {
                backgroundColor: mode === m ? COLORS.black : COLORS.gray
              }
            }}
          >
            {m === 'single' && 'Enkel sökning'}
            {m === 'multi' && 'Multi-adress'}
            {m === 'csv' && 'CSV-matchning'}
          </Button>
        ))}
      </Box>

      <Box sx={SECTION_STYLES.card}>
        {/* Adressfält för single mode */}
        {(mode === 'single' || mode === 'csv') && (
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <TextField
              placeholder="Gatuadress"
              value={street}
              onChange={(e) => setStreet(e.target.value)}
              sx={{
                flex: 1,
                minWidth: 200,
                '& .MuiOutlinedInput-input': { py: 1.5 }
              }}
            />
            <TextField
              placeholder="Ort"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              sx={{
                flex: 1,
                minWidth: 150,
                '& .MuiOutlinedInput-input': { py: 1.5 }
              }}
            />
            <TextField
              placeholder="Postnummer (valfritt)"
              value={zipcode}
              onChange={(e) => setZipcode(e.target.value)}
              sx={{
                flex: 1,
                minWidth: 120,
                '& .MuiOutlinedInput-input': { py: 1.5 }
              }}
            />
          </Box>
        )}

        {/* Multi-adress textarea */}
        {mode === 'multi' && (
          <Box sx={{ mb: 2 }}>
            <TextField
              multiline
              rows={6}
              fullWidth
              value={multiAddresses}
              onChange={(e) => setMultiAddresses(e.target.value)}
              placeholder="Ange adresser (en per rad)&#10;Storgatan 15, Stockholm&#10;Kungsgatan 10, Göteborg"
              helperText="Format: Gatuadress, Ort"
            />
          </Box>
        )}

        {/* CSV-uppladdning med drag-and-drop */}
        {mode === 'csv' && (
          <Box
            sx={{
              mb: 2,
              p: 4,
              border: isDragging ? `3px dashed ${COLORS.primary}` : `2px dashed ${COLORS.darkGray}`,
              borderRadius: '12px',
              textAlign: 'center',
              bgcolor: isDragging ? COLORS.lightGray : 'transparent',
              transition: 'all 0.2s ease',
              cursor: 'pointer'
            }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
              ref={fileInputRef}
            />
            <CloudUploadIcon sx={{ fontSize: 48, color: isDragging ? COLORS.primary : COLORS.lightBlack, mb: 1 }} />
            <Typography sx={{ fontWeight: isDragging ? 700 : 400, fontFamily: "'Poppins', sans-serif" }}>
              {isDragging ? 'Släpp filen här' : 'Dra och släpp CSV/Excel-fil här'}
            </Typography>
            <Typography sx={{ color: COLORS.lightBlack, fontFamily: "'Poppins', sans-serif", fontSize: '14px' }}>
              eller klicka för att välja fil
            </Typography>
            {csvData.length > 0 && (
              <Chip
                label={`${csvData.length} rader laddade`}
                sx={{ mt: 2, bgcolor: COLORS.green, color: COLORS.white }}
              />
            )}
          </Box>
        )}

        {/* Kolumnmappning för CSV */}
        {mode === 'csv' && csvColumns.length > 0 && (
          <Box sx={{ mb: 2, p: 2, bgcolor: COLORS.gray, borderRadius: '12px' }}>
            <Typography sx={{ mb: 2, fontWeight: 700, fontFamily: "'Poppins', sans-serif" }}>Mappa kolumner:</Typography>

            {/* Namnformat-väljare */}
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ mb: 1, fontFamily: "'Poppins', sans-serif", fontSize: '14px' }}>Namnformat:</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  onClick={() => setNameFormat('combined')}
                  backgroundColor={nameFormat === 'combined' ? COLORS.black : COLORS.white}
                  textcolor={nameFormat === 'combined' ? COLORS.white : COLORS.black}
                  sx={{ fontSize: '12px', padding: '8px 16px' }}
                >
                  Kombinerat namn (en kolumn)
                </Button>
                <Button
                  onClick={() => setNameFormat('separate')}
                  backgroundColor={nameFormat === 'separate' ? COLORS.black : COLORS.white}
                  textcolor={nameFormat === 'separate' ? COLORS.white : COLORS.black}
                  sx={{ fontSize: '12px', padding: '8px 16px' }}
                >
                  Separata kolumner (förnamn + efternamn)
                </Button>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {nameFormat === 'combined' ? (
                <Box>
                  <Typography sx={{ fontSize: '14px', fontWeight: 600, color: COLORS.lightBlack, mb: 0.5 }}>Namn</Typography>
                  <TextField
                    select
                    value={columnMapping.name ?? ''}
                    onChange={(e) => setColumnMapping({ ...columnMapping, name: parseInt(e.target.value) })}
                    SelectProps={{ native: true }}
                    sx={{ minWidth: 200 }}
                  >
                    <option value="">-- Välj kolumn --</option>
                    {csvColumns.map((col, i) => (
                      <option key={i} value={i}>{col}</option>
                    ))}
                  </TextField>
                  {columnMapping.name >= 0 && (
                    <Box sx={{ mt: 0.5, fontSize: '12px', color: COLORS.lightBlack }}>
                      Ex: {getColumnPreview(columnMapping.name).join(', ')}
                    </Box>
                  )}
                </Box>
              ) : (
                <>
                  <Box>
                    <Typography sx={{ fontSize: '14px', fontWeight: 600, color: COLORS.lightBlack, mb: 0.5 }}>Förnamn</Typography>
                    <TextField
                      select
                      value={columnMapping.firstName ?? ''}
                      onChange={(e) => setColumnMapping({ ...columnMapping, firstName: parseInt(e.target.value) })}
                      SelectProps={{ native: true }}
                      sx={{ minWidth: 150 }}
                    >
                      <option value="">-- Välj kolumn --</option>
                      {csvColumns.map((col, i) => (
                        <option key={i} value={i}>{col}</option>
                      ))}
                    </TextField>
                    {columnMapping.firstName >= 0 && (
                      <Box sx={{ mt: 0.5, fontSize: '12px', color: COLORS.lightBlack }}>
                        Ex: {getColumnPreview(columnMapping.firstName).join(', ')}
                      </Box>
                    )}
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '14px', fontWeight: 600, color: COLORS.lightBlack, mb: 0.5 }}>Efternamn</Typography>
                    <TextField
                      select
                      value={columnMapping.lastName ?? ''}
                      onChange={(e) => setColumnMapping({ ...columnMapping, lastName: parseInt(e.target.value) })}
                      SelectProps={{ native: true }}
                      sx={{ minWidth: 150 }}
                    >
                      <option value="">-- Välj kolumn --</option>
                      {csvColumns.map((col, i) => (
                        <option key={i} value={i}>{col}</option>
                      ))}
                    </TextField>
                    {columnMapping.lastName >= 0 && (
                      <Box sx={{ mt: 0.5, fontSize: '12px', color: COLORS.lightBlack }}>
                        Ex: {getColumnPreview(columnMapping.lastName).join(', ')}
                      </Box>
                    )}
                  </Box>
                </>
              )}

              <Box>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: COLORS.lightBlack, mb: 0.5 }}>Lägenhetsnr (valfritt)</Typography>
                <TextField
                  select
                  value={columnMapping.apartment ?? ''}
                  onChange={(e) => setColumnMapping({ ...columnMapping, apartment: parseInt(e.target.value) })}
                  SelectProps={{ native: true }}
                  sx={{ minWidth: 180 }}
                >
                  <option value="">-- Ingen --</option>
                  {csvColumns.map((col, i) => (
                    <option key={i} value={i}>{col}</option>
                  ))}
                </TextField>
                {columnMapping.apartment >= 0 && (
                  <Box sx={{ mt: 0.5, fontSize: '12px', color: COLORS.lightBlack }}>
                    Ex: {getColumnPreview(columnMapping.apartment).join(', ')}
                  </Box>
                )}
              </Box>

              <Box>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: COLORS.lightBlack, mb: 0.5 }}>Adress (för filtrering)</Typography>
                <TextField
                  select
                  value={columnMapping.address ?? ''}
                  onChange={(e) => setColumnMapping({ ...columnMapping, address: parseInt(e.target.value) })}
                  SelectProps={{ native: true }}
                  sx={{ minWidth: 180 }}
                >
                  <option value="">-- Ingen --</option>
                  {csvColumns.map((col, i) => (
                    <option key={i} value={i}>{col}</option>
                  ))}
                </TextField>
                {columnMapping.address >= 0 && (
                  <Box sx={{ mt: 0.5, fontSize: '12px', color: COLORS.lightBlack }}>
                    Ex: {getColumnPreview(columnMapping.address).join(', ')}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        )}

        {/* Sökknapp */}
        <Button
          isLoading={loading}
          onClick={
            mode === 'single' ? handleSingleSearch :
            mode === 'multi' ? handleMultiSearch :
            runCsvMatching
          }
          disabled={loading}
          backgroundColor={COLORS.primary}
          sx={{ mr: 2 }}
        >
          <SearchIcon sx={{ mr: 1 }} />
          {mode === 'single' ? 'Sök folkbokförda' :
           mode === 'multi' ? 'Sök alla adresser' :
           'Matcha mot API'}
        </Button>

        {/* Progressbar för multi-sökning */}
        {loading && mode === 'multi' && multiProgress.total > 0 && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '14px' }}>
                Söker adress {multiProgress.current} av {multiProgress.total}...
              </Typography>
              <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '14px' }}>
                {Math.round((multiProgress.current / multiProgress.total) * 100)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={(multiProgress.current / multiProgress.total) * 100}
              sx={{ height: 8, borderRadius: 4, bgcolor: COLORS.gray, '& .MuiLinearProgress-bar': { bgcolor: COLORS.primary } }}
            />
          </Box>
        )}

        {/* Testexempel */}
        {mode !== 'multi' && (
          <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${COLORS.darkGray}`, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ color: COLORS.lightBlack, fontFamily: "'Poppins', sans-serif", fontSize: '14px' }}>Testexempel:</Typography>
            <Button
              onClick={() => fillExample('Timmermansgatan 27', 'Stockholm', '11855')}
              backgroundColor={COLORS.gray}
              textcolor={COLORS.black}
              sx={{ fontSize: '12px', padding: '6px 12px' }}
            >
              Timmermansgatan 27, Stockholm
            </Button>
            <Button
              onClick={() => fillExample('Kungsgatan 10', 'Göteborg', '')}
              backgroundColor={COLORS.gray}
              textcolor={COLORS.black}
              sx={{ fontSize: '12px', padding: '6px 12px' }}
            >
              Kungsgatan 10, Göteborg
            </Button>
          </Box>
        )}
      </Box>

      {/* Felmeddelande */}
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>{error}</Alert>
      )}

      {/* Sammanfattningsbox - CSV Mode */}
      {mode === 'csv' && stats && matchResults.length > 0 && (
        <Box sx={{ ...SECTION_STYLES.card, bgcolor: COLORS.lightGray }}>
          <Typography sx={{ mb: 2, fontWeight: 700, fontFamily: "'Poppins', sans-serif", fontSize: '18px' }}>Sammanfattning</Typography>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Box sx={{
              ...SECTION_STYLES.statBox,
              bgcolor: stats.discrepancy > 0 ? COLORS.lightRed : COLORS.white,
              border: stats.discrepancy > 0 ? `2px solid ${COLORS.red}` : `1px solid ${COLORS.darkGray}`
            }}>
              <Typography sx={{ fontSize: '36px', fontWeight: 700, color: COLORS.red, fontFamily: "'Poppins', sans-serif" }}>
                {stats.discrepancy}
              </Typography>
              <Typography sx={{ fontSize: '12px', fontWeight: 600, color: COLORS.red, fontFamily: "'Poppins', sans-serif" }}>
                MISSTÄNKT OLOVLIG
              </Typography>
            </Box>
            <Box sx={SECTION_STYLES.statBox}>
              <Typography sx={{ fontSize: '36px', fontWeight: 700, color: COLORS.green, fontFamily: "'Poppins', sans-serif" }}>
                {stats.match}
              </Typography>
              <Typography sx={{ fontSize: '12px', fontWeight: 600, color: COLORS.green, fontFamily: "'Poppins', sans-serif" }}>
                BEKRÄFTADE MATCHER
              </Typography>
            </Box>
            <Box sx={SECTION_STYLES.statBox}>
              <Typography sx={{ fontSize: '36px', fontWeight: 700, color: '#f57c00', fontFamily: "'Poppins', sans-serif" }}>
                {stats.csvOnly}
              </Typography>
              <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#f57c00', fontFamily: "'Poppins', sans-serif" }}>
                EJ FOLKBOKFÖRDA
              </Typography>
            </Box>
            <Box sx={SECTION_STYLES.statBox}>
              <Typography sx={{ fontSize: '36px', fontWeight: 700, color: COLORS.lightBlack, fontFamily: "'Poppins', sans-serif" }}>
                {stats.apiOnly}
              </Typography>
              <Typography sx={{ fontSize: '12px', fontWeight: 600, color: COLORS.lightBlack, fontFamily: "'Poppins', sans-serif" }}>
                ENDAST FOLKBOKF.
              </Typography>
            </Box>
          </Box>

          {/* Import-knapp */}
          {selectedRows.size > 0 && (
            <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${COLORS.darkGray}` }}>
              <Button
                backgroundColor={COLORS.primary}
                onClick={handleImport}
              >
                Importera {selectedRows.size} markerade poster
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* Statistik - Single Mode */}
      {mode === 'single' && stats && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <Box sx={SECTION_STYLES.statBox}>
            <Typography sx={{ fontSize: '28px', fontWeight: 700, color: COLORS.primary, fontFamily: "'Poppins', sans-serif" }}>{stats.matching}</Typography>
            <Typography sx={{ fontSize: '12px', color: COLORS.lightBlack, fontFamily: "'Poppins', sans-serif" }}>Matchande</Typography>
          </Box>
          <Box sx={SECTION_STYLES.statBox}>
            <Typography sx={{ fontSize: '28px', fontWeight: 700, color: COLORS.black, fontFamily: "'Poppins', sans-serif" }}>{stats.withApt}</Typography>
            <Typography sx={{ fontSize: '12px', color: COLORS.lightBlack, fontFamily: "'Poppins', sans-serif" }}>Med lgh-nr</Typography>
          </Box>
          <Box sx={SECTION_STYLES.statBox}>
            <Typography sx={{ fontSize: '28px', fontWeight: 700, color: COLORS.black, fontFamily: "'Poppins', sans-serif" }}>{stats.withoutApt}</Typography>
            <Typography sx={{ fontSize: '12px', color: COLORS.lightBlack, fontFamily: "'Poppins', sans-serif" }}>Utan lgh-nr</Typography>
          </Box>
          <Box sx={SECTION_STYLES.statBox}>
            <Typography sx={{ fontSize: '28px', fontWeight: 700, color: COLORS.lightBlack, fontFamily: "'Poppins', sans-serif" }}>{stats.total}</Typography>
            <Typography sx={{ fontSize: '12px', color: COLORS.lightBlack, fontFamily: "'Poppins', sans-serif" }}>Totalt i området</Typography>
          </Box>
          <Box sx={SECTION_STYLES.statBox}>
            <Typography sx={{ fontSize: '28px', fontWeight: 700, color: COLORS.lightBlack, fontFamily: "'Poppins', sans-serif" }}>{stats.fetched}</Typography>
            <Typography sx={{ fontSize: '12px', color: COLORS.lightBlack, fontFamily: "'Poppins', sans-serif" }}>Hämtade</Typography>
          </Box>
        </Box>
      )}

      {/* Statistik - Multi Mode */}
      {mode === 'multi' && stats && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <Box sx={SECTION_STYLES.statBox}>
            <Typography sx={{ fontSize: '28px', fontWeight: 700, color: COLORS.primary, fontFamily: "'Poppins', sans-serif" }}>{stats.addresses}</Typography>
            <Typography sx={{ fontSize: '12px', color: COLORS.lightBlack, fontFamily: "'Poppins', sans-serif" }}>Adresser</Typography>
          </Box>
          <Box sx={SECTION_STYLES.statBox}>
            <Typography sx={{ fontSize: '28px', fontWeight: 700, color: COLORS.green, fontFamily: "'Poppins', sans-serif" }}>{stats.successful}</Typography>
            <Typography sx={{ fontSize: '12px', color: COLORS.lightBlack, fontFamily: "'Poppins', sans-serif" }}>Lyckade</Typography>
          </Box>
          <Box sx={SECTION_STYLES.statBox}>
            <Typography sx={{ fontSize: '28px', fontWeight: 700, color: COLORS.black, fontFamily: "'Poppins', sans-serif" }}>{stats.totalResidents}</Typography>
            <Typography sx={{ fontSize: '12px', color: COLORS.lightBlack, fontFamily: "'Poppins', sans-serif" }}>Totalt boende</Typography>
          </Box>
          <Box sx={SECTION_STYLES.statBox}>
            <Typography sx={{ fontSize: '28px', fontWeight: 700, color: COLORS.black, fontFamily: "'Poppins', sans-serif" }}>{stats.withApt}</Typography>
            <Typography sx={{ fontSize: '12px', color: COLORS.lightBlack, fontFamily: "'Poppins', sans-serif" }}>Med lgh-nr</Typography>
          </Box>
        </Box>
      )}

      {/* Resultat - Enkel sökning */}
      {mode === 'single' && results && results.length > 0 && (
        <TableContainer sx={{ ...SECTION_STYLES.card, p: 0 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: COLORS.black }}>
                <TableCell sx={{ color: COLORS.white, fontFamily: "'Poppins', sans-serif" }}>Lägenhet</TableCell>
                <TableCell sx={{ color: COLORS.white, fontFamily: "'Poppins', sans-serif" }}>Namn</TableCell>
                <TableCell sx={{ color: COLORS.white, fontFamily: "'Poppins', sans-serif" }}>Adress</TableCell>
                <TableCell sx={{ color: COLORS.white, fontFamily: "'Poppins', sans-serif" }}>Postnummer</TableCell>
                <TableCell sx={{ color: COLORS.white, fontFamily: "'Poppins', sans-serif" }}>Ålder</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {results.map((person, i) => (
                <TableRow key={i} hover>
                  <TableCell>
                    {person.apartmentNumber ? (
                      <Chip label={person.apartmentNumber} sx={{ bgcolor: COLORS.primary, color: COLORS.white }} size="small" />
                    ) : (
                      <Typography sx={{ color: COLORS.lightBlack, fontStyle: 'italic', fontSize: '14px' }}>Saknas</Typography>
                    )}
                  </TableCell>
                  <TableCell><strong>{person.firstName} {person.lastName}</strong></TableCell>
                  <TableCell>{person.street}</TableCell>
                  <TableCell>{person.zipCode} {person.city}</TableCell>
                  <TableCell>{person.age}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Resultat - Multi-adress */}
      {mode === 'multi' && multiResults.length > 0 && (
        <Box>
          {multiResults.map((result, idx) => (
            <Box key={idx} sx={{ ...SECTION_STYLES.card, p: 0, overflow: 'hidden' }}>
              <Box sx={{
                p: 2,
                bgcolor: result.success ? COLORS.lightGreen : COLORS.lightRed,
                borderBottom: `1px solid ${COLORS.darkGray}`
              }}>
                <Typography sx={{ fontWeight: 700, fontFamily: "'Poppins', sans-serif" }}>
                  {result.address}
                </Typography>
                {result.success ? (
                  <Typography sx={{ color: COLORS.lightBlack, fontSize: '14px', fontFamily: "'Poppins', sans-serif" }}>
                    {result.residents.length} folkbokförda ({result.stats.withApt} med lgh-nr)
                  </Typography>
                ) : (
                  <Typography sx={{ color: COLORS.red, fontSize: '14px', fontFamily: "'Poppins', sans-serif" }}>
                    {result.error}
                  </Typography>
                )}
              </Box>
              {result.success && result.residents.length > 0 && (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Lgh</TableCell>
                        <TableCell>Namn</TableCell>
                        <TableCell>Adress</TableCell>
                        <TableCell>Ålder</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {result.residents.slice(0, 10).map((person, i) => (
                        <TableRow key={i} hover>
                          <TableCell>
                            {person.apartmentNumber ? (
                              <Chip label={person.apartmentNumber} sx={{ bgcolor: COLORS.primary, color: COLORS.white }} size="small" />
                            ) : '-'}
                          </TableCell>
                          <TableCell><strong>{person.firstName} {person.lastName}</strong></TableCell>
                          <TableCell>{person.street}</TableCell>
                          <TableCell>{person.age}</TableCell>
                        </TableRow>
                      ))}
                      {result.residents.length > 10 && (
                        <TableRow>
                          <TableCell colSpan={4} sx={{ textAlign: 'center', color: COLORS.lightBlack }}>
                            ... och {result.residents.length - 10} fler
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          ))}
        </Box>
      )}

      {/* Resultat - CSV-matchning med kollapserbara sektioner */}
      {mode === 'csv' && matchResults.length > 0 && (
        <Box>
          {/* MISSTÄNKT OLOVLIG UTHYRNING - Röd */}
          <CollapsibleSection
            title="MISSTÄNKT OLOVLIG UTHYRNING"
            count={stats.discrepancy}
            color={COLORS.red}
            bgColor={COLORS.lightRed}
            borderColor={COLORS.red}
            icon={<WarningIcon sx={{ color: COLORS.red }} />}
            defaultExpanded={true}
          >
            {renderResultTable('discrepancy')}
          </CollapsibleSection>

          {/* BEKRÄFTADE MATCHER - Grön */}
          <CollapsibleSection
            title="BEKRÄFTADE MATCHER"
            count={stats.match}
            color={COLORS.green}
            bgColor={COLORS.lightGreen}
            borderColor={COLORS.green}
            icon={<CheckCircleIcon sx={{ color: COLORS.green }} />}
            defaultExpanded={false}
          >
            {renderResultTable('match')}
          </CollapsibleSection>

          {/* EJ FOLKBOKFÖRDA - Orange */}
          <CollapsibleSection
            title="EJ FOLKBOKFÖRDA"
            count={stats.csvOnly}
            color="#f57c00"
            bgColor={COLORS.lightOrange}
            borderColor="#ff9800"
            icon={<ErrorOutlineIcon sx={{ color: '#f57c00' }} />}
            defaultExpanded={false}
          >
            {renderResultTable('csv_only')}
          </CollapsibleSection>

          {/* ENDAST I FOLKBOKFÖRING - Grå */}
          <CollapsibleSection
            title="ENDAST I FOLKBOKFÖRING"
            count={stats.apiOnly}
            color={COLORS.lightBlack}
            bgColor={COLORS.gray}
            borderColor={COLORS.darkGray}
            icon={<ErrorOutlineIcon sx={{ color: COLORS.lightBlack }} />}
            defaultExpanded={false}
          >
            {renderResultTable('api_only', false)}
          </CollapsibleSection>
        </Box>
      )}
    </Box>
  );
}
